import { ClientOptionsApi, ClientDetail } from '../../core/client-options-api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClientSelect } from '../../core/client-select';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { finalize, of, Subscription, switchMap, map } from 'rxjs';
import { PageNavigation } from '../../shared/page-navigation/page-navigation';
import {
  PosClientInstallmentsPlan,
  PosClientInstallmentsPlanSaveRequest,
  PosInstallments,
  PosInstallmentsPlan,
  PosInstallmentsPlanFactor,
  PosInstallmentsPlanFactorSaveRequest,
  PosInstallmentsPlanSaveRequest,
  PosInstallmentsSaveRequest,
  PosMerchantConditionFee,
  PosMerchantConditionFeeSaveRequest,
  PosMerchantId,
  PosMerchantIdSaveRequest,
  PosTerminal,
  PosTerminalGP,
  PosTerminalGPSaveRequest,
  PosTerminalPayway,
  PosTerminalPaywaySaveRequest,
  PosTerminalSaveRequest,
  PosVirtualTerminal,
  PosVirtualTerminalMedioPago,
  PosVirtualTerminalMedioPagoSaveRequest,
  PosVirtualTerminalSaveRequest,
  TerminalConfigurationSummary,
  TerminalFilterValue,
  TerminalManagementApi
  ,Province
	,Locality
  ,SubmerchantData
  ,SubmerchantSaveRequest
  ,SubmerchantSummary
} from '../../core/terminal-management-api';

type TerminalTab =
  | 'pos-terminals'
  | 'merchant-id'
  | 'condition-fee'
  | 'installments'
  | 'installments-plan'
  | 'plan-installments'
  | 'assign-plan'
  | 'terminal-payway'
  | 'terminal-global-processing'
  | 'virtual-terminal'
  | 'configuration'
  | 'submerchant';
type TerminalAction = 'create' | 'update' | 'delete';
type ManagementSection = 'terminals' | 'terminal-configuration' | 'payway' | 'global-processing';

@Component({
  selector: 'app-terminals',
  imports: [CommonModule, FormsModule, PageNavigation, ClientSelect],
  templateUrl: './terminals.html'
})
export class Terminals implements OnInit {
  readonly section: ManagementSection;
  readonly tabs: Array<{ id: TerminalTab; label: string }>;
  readonly acquirers = ['PAYWAY', 'GLOBAL_PROCESSING'];
  readonly virtualTerminalTypes = ['CP', 'CNP'] as const;
  readonly selectedAcquirer?: string;
  readonly pageTitle: string;
  private readonly commercialTabs: Array<{ id: TerminalTab; label: string }> = [
    { id: 'merchant-id', label: 'Merchant ID' },
    { id: 'condition-fee', label: 'Merchant Condition Fee' },
    { id: 'installments', label: 'Installments' },
    { id: 'installments-plan', label: 'Plan' },
    { id: 'plan-installments', label: 'Plan Installments' },
    { id: 'assign-plan', label: 'Asignar plan' },
    { id: 'configuration', label: 'Configuracion' }
  ];
  readonly weekDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  readonly statuses = ['FUNCIONAL', 'DAÑADO', 'SIN_FUNCIONAR'];
  readonly virtualStatuses = [
    'ONLINE',
    'OFFLINE',
    'DEGRADED',
    'SYSTEM_DOWN',
    'TERMINAL_ERROR',
    'HOST_UNAVAILABLE',
    'NETWORK_ERROR',
    'CERTIFICATION_MODE',
    'MAINTENANCE',
    'BLOCKED',
    'SUSPENDED',
    'UNPROVISIONED',
    'UNKNOWN'
  ];
  readonly cardBrands = ['VISA', 'MASTERCARD', 'AMEX', 'CABAL', 'MAESTRO', 'NARANJA', 'UNKNOWN'];
  readonly paymentProcessTypes = ['PAYFAC', 'GATEWAY', 'PAYFAC_WHITELABEL', 'GATEWAY_WHITELABEL', 'DIRECT'];
  readonly paymentTypes = ['CREDIT', 'DEBIT', 'PREPAID', 'DEBIT_PREPAID', 'CHARGE', 'FLEET'];
  readonly installmentTypes = ['ONE_PAYMENT', 'INSTALLMENTS', 'NOT_APPLICABLE'];
  readonly pageSizes = [10, 25, 50, 100];

  activeTab: TerminalTab = 'pos-terminals';
  rows: PosTerminal[] = [];
  filters: Record<string, TerminalFilterValue> = {};
  searchText = '';
  page = 0;
  size = 10;
  total = 0;
  totalRecords = 0;

  form: PosTerminalSaveRequest = this.emptyForm();
  editing?: PosTerminal;
  merchantRows: PosMerchantId[] = [];
  merchantFilters: Record<string, TerminalFilterValue> = {};
  merchantSearchText = '';
  merchantPage = 0;
  merchantSize = 10;
  merchantTotal = 0;
  merchantForm: PosMerchantIdSaveRequest = this.emptyMerchantForm();
  editingMerchant?: PosMerchantId;
  merchantOptions: PosMerchantId[] = [];
  conditionRows: PosMerchantConditionFee[] = [];
  conditionFilters: Record<string, TerminalFilterValue> = {};
  conditionSearchText = '';
  conditionPage = 0;
  conditionSize = 10;
  conditionTotal = 0;
  conditionForm: PosMerchantConditionFeeSaveRequest = this.emptyConditionForm();
  editingCondition?: PosMerchantConditionFee;
  installmentRows: PosInstallments[] = [];
  installmentFilters: Record<string, TerminalFilterValue> = {};
  installmentSearchText = '';
  installmentPage = 0;
  installmentSize = 10;
  installmentTotal = 0;
  installmentForm: PosInstallmentsSaveRequest = this.emptyInstallmentForm();
  editingInstallment?: PosInstallments;
  installmentConditionFeeOptions: PosMerchantConditionFee[] = [];
  planRows: PosInstallmentsPlan[] = [];
  planFilters: Record<string, TerminalFilterValue> = {};
  planSearchText = '';
  planPage = 0;
  planSize = 10;
  planTotal = 0;
  planForm: PosInstallmentsPlanSaveRequest = this.emptyPlanForm();
  editingPlan?: PosInstallmentsPlan;
  planBinDraft = '';
  planDayDraft = '';
  planOptions: PosInstallmentsPlan[] = [];
  planFactorRows: PosInstallmentsPlanFactor[] = [];
  planFactorFilters: Record<string, TerminalFilterValue> = {};
  planFactorSearchText = '';
  planFactorPage = 0;
  planFactorSize = 10;
  planFactorTotal = 0;
  planFactorForm: PosInstallmentsPlanFactorSaveRequest = this.emptyPlanFactorForm();
  editingPlanFactor?: PosInstallmentsPlanFactor;
  assignPlanRows: PosClientInstallmentsPlan[] = [];
  assignPlanFilters: Record<string, TerminalFilterValue> = {};
  assignPlanSearchText = '';
  assignPlanPage = 0;
  assignPlanSize = 10;
  assignPlanTotal = 0;
  assignPlanForm: PosClientInstallmentsPlanSaveRequest = this.emptyAssignPlanForm();
  editingAssignPlan?: PosClientInstallmentsPlan;
  virtualRows: PosVirtualTerminal[] = [];
  virtualFilters: Record<string, TerminalFilterValue> = {};
  virtualSearchText = '';
  virtualPage = 0;
  virtualSize = 10;
  virtualTotal = 0;
  virtualForm: PosVirtualTerminalSaveRequest = this.emptyVirtualForm();
  editingVirtual?: PosVirtualTerminal;
  availablePosTerminals: PosTerminal[] = [];
  virtualMedioPagoRows: PosVirtualTerminalMedioPago[] = [];
  virtualMedioPagoFilters: Record<string, TerminalFilterValue> = {};
  virtualMedioPagoSearchText = '';
  virtualMedioPagoPage = 0;
  virtualMedioPagoSize = 10;
  virtualMedioPagoTotal = 0;
  virtualMedioPagoForm: PosVirtualTerminalMedioPagoSaveRequest = this.emptyVirtualMedioPagoForm();
  editingVirtualMedioPago?: PosVirtualTerminalMedioPago;
  terminalPaywayRows: PosTerminalPayway[] = [];
  terminalPaywayFilters: Record<string, TerminalFilterValue> = {};
  terminalPaywaySearchText = '';
  terminalPaywayPage = 0;
  terminalPaywaySize = 10;
  terminalPaywayTotal = 0;
  terminalPaywayForm: PosTerminalPaywaySaveRequest = this.emptyTerminalPaywayForm();
  editingTerminalPayway?: PosTerminalPayway;
  terminalGPRows: PosTerminalGP[] = [];
  terminalGPFilters: Record<string, TerminalFilterValue> = {};
  terminalGPSearchText = '';
  terminalGPPage = 0;
  terminalGPSize = 10;
  terminalGPTotal = 0;
  terminalGPForm: PosTerminalGPSaveRequest = this.emptyTerminalGPForm();
  editingTerminalGP?: PosTerminalGP;
  configurationRows: TerminalConfigurationSummary[] = [];
  configurationFilters: Record<string, TerminalFilterValue> = {};
  configurationSearchText = '';
  configurationPage = 0;
  configurationSize = 10;
  configurationTotal = 0;
  configurationDetail?: Record<string, unknown>;
  configurationDetailRow?: TerminalConfigurationSummary;
  configurationDetailLoading = false;
  virtualTerminalOptions: PosVirtualTerminal[] = [];
  private virtualPosTerminalSerials = new Map<number, string>();
  modalOpen = false;
  merchantModalOpen = false;
  conditionModalOpen = false;
  installmentModalOpen = false;
  planModalOpen = false;
  planFactorModalOpen = false;
  assignPlanModalOpen = false;
  virtualModalOpen = false;
  virtualMedioPagoModalOpen = false;
  terminalPaywayModalOpen = false;
  terminalGPModalOpen = false;
  tableLoading = false;
  loadingAction?: TerminalAction;
  message = '';
  error = '';

  submerchantRows: SubmerchantSummary[] = [];
  submerchantOptions: SubmerchantSummary[] = [];
  submerchantPage = 0;
  submerchantSize = 10;
  submerchantTotal = 0;
  private readonly clientOptionsApi = inject(ClientOptionsApi);
  private readonly destroyRef = inject(DestroyRef);
  private submerchantClientRequest?: Subscription;
  submerchantClientLoading = false;
  submerchantClientError = '';
  submerchantClientNotice = '';
  submerchantModalOpen = false;
  submerchantSaving = false;
  editingSubmerchant?: SubmerchantSummary;
  provinces: Province[] = [];
	cities: Locality[] = [];
  selectedProvinceId: number | null = null;
  submerchantForm: SubmerchantSaveRequest = this.emptySubmerchantForm();

  constructor(
    private readonly api: TerminalManagementApi,
    private readonly cdr: ChangeDetectorRef,
    route: ActivatedRoute
  ) {
    this.section = (route.snapshot.data['section'] as ManagementSection | undefined) ?? 'terminals';
    this.selectedAcquirer = this.section === 'payway' ? 'PAYWAY' : this.section === 'global-processing' ? 'GLOBAL_PROCESSING' : undefined;
    this.pageTitle = this.section === 'terminal-configuration'
      ? 'Configurar terminal'
      : this.section === 'payway'
        ? 'PAYWAY'
        : this.section === 'global-processing'
          ? 'Global Processing'
          : 'Terminales';
    this.tabs = this.section === 'terminals'
      ? [{ id: 'pos-terminals', label: 'Terminales POS' }, { id: 'virtual-terminal', label: 'Terminal Virtual' }]
      : this.section === 'terminal-configuration'
        ? this.commercialTabs
        : this.section === 'payway'
          ? [{ id: 'terminal-payway', label: 'Terminal Payway' }]
          : [
              { id: 'submerchant', label: 'Submerchant' },
              { id: 'terminal-global-processing', label: 'Terminal Global Processing' }
            ];
    const requestedTab = route.snapshot.data['activeTab'] as TerminalTab | undefined;
    this.activeTab = requestedTab && this.tabs.some(tab => tab.id === requestedTab)
      ? requestedTab
      : this.tabs[0].id;
    this.merchantForm = this.emptyMerchantForm();
    this.virtualForm = this.emptyVirtualForm();
  }

  ngOnInit(): void {
    this.selectTab(this.activeTab);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.size));
  }

  get isEditing(): boolean {
    return Boolean(this.editing);
  }

  get canSave(): boolean {
    return Boolean(this.form.serialNumber.trim() && this.form.softwareVersion.trim() && this.form.terminalStatus);
  }

  get activeTabLabel(): string {
    return this.tabs.find(tab => tab.id === this.activeTab)?.label ?? 'Terminales';
  }

  get merchantTotalPages(): number {
    return Math.max(1, Math.ceil(this.merchantTotal / this.merchantSize));
  }

  get isEditingMerchant(): boolean {
    return Boolean(this.editingMerchant);
  }

  get canSaveMerchant(): boolean {
    return Boolean(
      this.merchantForm.merchantId.trim() &&
        this.merchantForm.mcc.trim() &&
        this.merchantForm.cardBrand &&
        this.merchantForm.paymentProcessType
    );
  }

  get conditionMerchantOptions(): PosMerchantId[] {
    const enabledOptions = this.merchantOptions.filter(merchant => merchant.enabled);
    if (!this.isEditingCondition || !this.conditionForm.posMerchantId) {
      return enabledOptions;
    }
    const selected = this.merchantOptions.find(merchant => merchant.id === this.conditionForm.posMerchantId);
    if (!selected || selected.enabled || enabledOptions.some(merchant => merchant.id === selected.id)) {
      return enabledOptions;
    }
    return [selected, ...enabledOptions];
  }

  get conditionTotalPages(): number {
    return Math.max(1, Math.ceil(this.conditionTotal / this.conditionSize));
  }

  get isEditingCondition(): boolean {
    return Boolean(this.editingCondition);
  }

  get isConditionInstallmentTypeFixed(): boolean {
    return this.conditionForm.paymentType === 'DEBIT' || this.conditionForm.paymentType === 'PREPAID';
  }

  onConditionPaymentTypeChange(): void {
    if (this.isConditionInstallmentTypeFixed) {
      this.conditionForm.installmentType = 'NOT_APPLICABLE';
    }
  }

  get canSaveCondition(): boolean {
    return Boolean(
      this.conditionForm.posMerchantId &&
        this.conditionForm.paymentType &&
        this.conditionForm.installmentType &&
        this.conditionForm.tax !== null &&
        this.conditionForm.fee !== null &&
        this.conditionForm.tna !== null &&
        this.conditionForm.bankFee !== null
    );
  }

  get installmentTotalPages(): number {
    return Math.max(1, Math.ceil(this.installmentTotal / this.installmentSize));
  }

  get isEditingInstallment(): boolean {
    return Boolean(this.editingInstallment);
  }

  get canSaveInstallment(): boolean {
    return Boolean(
      this.installmentForm.posMerchantConditionFeeId &&
        this.installmentForm.installmentFactor !== null &&
        this.installmentForm.installmentNumber &&
        this.installmentForm.start
    );
  }

  get planTotalPages(): number {
    return Math.max(1, Math.ceil(this.planTotal / this.planSize));
  }

  get isEditingPlan(): boolean {
    return Boolean(this.editingPlan);
  }

  get canSavePlan(): boolean {
    return Boolean(
      this.planForm.posMerchantConditionFeeId &&
        this.planForm.description.trim() &&
        this.planForm.start &&
        this.planForm.ending
    );
  }

  get planFactorTotalPages(): number {
    return Math.max(1, Math.ceil(this.planFactorTotal / this.planFactorSize));
  }

  get isEditingPlanFactor(): boolean {
    return Boolean(this.editingPlanFactor);
  }

  get canSavePlanFactor(): boolean {
    return Boolean(
      this.planFactorForm.posInstallmentsPlanId &&
        this.planFactorForm.installmentFactor !== null &&
        this.planFactorForm.installmentNumber &&
        this.planFactorForm.displayText.trim()
    );
  }

  get assignPlanTotalPages(): number {
    return Math.max(1, Math.ceil(this.assignPlanTotal / this.assignPlanSize));
  }

  get isEditingAssignPlan(): boolean {
    return Boolean(this.editingAssignPlan);
  }

  get canSaveAssignPlan(): boolean {
    return Boolean(this.assignPlanForm.clientId && this.assignPlanForm.posInstallmentsPlanId);
  }

  get virtualTotalPages(): number {
    return Math.max(1, Math.ceil(this.virtualTotal / this.virtualSize));
  }

  get isEditingVirtual(): boolean {
    return Boolean(this.editingVirtual);
  }

  get canSaveVirtual(): boolean {
    const hasRequiredFields = Boolean(
      this.virtualForm.terminalType
      && this.virtualForm.clientId
      && this.virtualForm.status
    );
    return hasRequiredFields
      && (this.virtualForm.terminalType === 'CNP' || Boolean(this.virtualForm.posTerminalId));
  }

  get virtualMedioPagoTotalPages(): number {
    return Math.max(1, Math.ceil(this.virtualMedioPagoTotal / this.virtualMedioPagoSize));
  }

  get isEditingVirtualMedioPago(): boolean {
    return Boolean(this.editingVirtualMedioPago);
  }

  get canSaveVirtualMedioPago(): boolean {
    return Boolean(this.virtualMedioPagoForm.pos_virutal_terminal_id && this.virtualMedioPagoForm.medio_pago_id);
  }

  get terminalPaywayTotalPages(): number {
    return Math.max(1, Math.ceil(this.terminalPaywayTotal / this.terminalPaywaySize));
  }

  get terminalGPTotalPages(): number {
    return Math.max(1, Math.ceil(this.terminalGPTotal / this.terminalGPSize));
  }

  get configurationTotalPages(): number {
    return Math.max(1, Math.ceil(this.configurationTotal / this.configurationSize));
  }

  get submerchantTotalPages(): number {
    return Math.max(1, Math.ceil(this.submerchantTotal / this.submerchantSize));
  }

  get canSaveSubmerchant(): boolean {
    if (this.submerchantClientLoading || this.submerchantClientError) return false;
    const data = this.submerchantForm.submerchantDTO;
    if (!data.softDescriptor?.trim() || data.softDescriptor.length > 18) return false;
	const phone = String(data.telefono ?? '');
	const websiteValid = !this.submerchantForm.cnp || /^(?=\S+$)(?=[^@]*[A-Za-z0-9]\.[A-Za-z]{2,}$).+$/.test(data.urlSitioWeb ?? '');
    return Boolean(
      this.submerchantForm.clientId && this.submerchantForm.idLocalidad && data.mcc &&
	  /^\d{4}$/.test(String(data.mcc)) &&
	  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.mailComercio) && /^\d{8,22}$/.test(phone) && websiteValid &&
      data.cuit && data.razonSocial.trim() && data.nombreComercial.trim() && data.calleNumero.trim() &&
	  /^[A-Za-z][0-9]{4}[A-Za-z]{3}$/.test(data.codPostal.trim()) && data.localidad.trim().length <= 20 &&
	  data.nombreTitular.trim() && data.apellidoTitular.trim() && data.documentoTitular &&
	  [1, 7, 8].includes(Number(data.tipoDocumento))
    );
  }

  get isEditingTerminalPayway(): boolean {
    return Boolean(this.editingTerminalPayway);
  }

  get canSaveTerminalPayway(): boolean {
    return Boolean(
      this.terminalPaywayForm.terminalId.trim() &&
        this.terminalPaywayForm.posVirtualTerminalId &&
        this.terminalPaywayForm.posMerchantId
    );
  }

  get isEditingTerminalGP(): boolean {
    return Boolean(this.editingTerminalGP);
  }

  get canSaveTerminalGP(): boolean {
    return Boolean(this.terminalGPForm.posVirtualTerminalId && this.terminalGPForm.submerchantDataId);
  }

  selectTab(tab: TerminalTab): void {
    this.activeTab = tab;
    if (tab === 'pos-terminals' && !this.rows.length) {
      this.loadPosTerminals();
    }
    if (tab === 'merchant-id' && !this.merchantRows.length) {
      this.loadMerchantIds();
    }
    if (tab === 'condition-fee') {
      this.ensureMerchantOptions();
      if (!this.conditionRows.length) {
        this.loadConditionFees();
      }
    }
    if (tab === 'installments') {
      this.ensureInstallmentConditionFeeOptions();
      if (!this.installmentRows.length) {
        this.loadInstallments();
      }
    }
    if (tab === 'installments-plan') {
      this.ensureInstallmentConditionFeeOptions();
      if (!this.planRows.length) {
        this.loadPlans();
      }
    }
    if (tab === 'plan-installments') {
      this.ensurePlanOptions();
      if (!this.planFactorRows.length) {
        this.loadPlanFactors();
      }
    }
    if (tab === 'assign-plan') {
      this.ensurePlanOptions();
      if (!this.assignPlanRows.length) {
        this.loadAssignPlans();
      }
    }
    if (tab === 'virtual-terminal') {
      this.ensureVirtualTerminalOptions();
      if (!this.virtualRows.length) {
        this.loadVirtualTerminals();
      }
      if (!this.virtualMedioPagoRows.length) {
        this.loadVirtualTerminalMedioPagos();
      }
    }
    if (tab === 'terminal-payway') {
      this.ensureMerchantOptions();
      this.ensureVirtualTerminalOptions();
      if (!this.terminalPaywayRows.length) {
        this.loadTerminalPayways();
      }
    }
    if (tab === 'configuration' && !this.configurationRows.length) {
      this.loadConfigurations();
    }
    if (tab === 'submerchant' && !this.submerchantRows.length) {
      this.loadSubmerchants();
    }
    if (tab === 'terminal-global-processing') {
      this.ensureVirtualTerminalOptions();
      this.ensureSubmerchantOptions();
      if (!this.terminalGPRows.length) {
        this.loadTerminalGPs();
      }
    }
  }

  openCreate(): void {
    this.form = this.emptyForm();
    this.editing = undefined;
    this.message = '';
    this.error = '';
    this.modalOpen = true;
  }

  openEdit(row: PosTerminal): void {
    this.form = {
      serialNumber: row.serialNumber ?? '',
      softwareVersion: row.softwareVersion ?? '',
      enabled: row.enabled ?? true,
      terminalStatus: row.terminalStatus ?? 'FUNCIONAL'
    };
    this.editing = row;
    this.message = '';
    this.error = '';
    this.modalOpen = true;
  }

  closeModal(): void {
    this.modalOpen = false;
    this.editing = undefined;
  }

  openMerchantCreate(): void {
    this.merchantForm = this.emptyMerchantForm();
    this.editingMerchant = undefined;
    this.message = '';
    this.error = '';
    this.merchantModalOpen = true;
  }

  openMerchantEdit(row: PosMerchantId): void {
    this.merchantForm = {
      clientId: row.clientId ?? null,
      merchantId: row.merchantId ?? '',
      enabled: row.enabled ?? true,
      descripcion: row.descripcion ?? '',
      mcc: row.mcc ?? '',
      cardBrand: row.cardBrand ?? 'VISA',
      paymentProcessType: row.paymentProcessType ?? 'PAYFAC',
      bankId: row.bankId ?? null,
      acquirer: row.acquirer ?? this.selectedAcquirer ?? 'PAYWAY'
    };
    this.editingMerchant = row;
    this.message = '';
    this.error = '';
    this.merchantModalOpen = true;
  }

  closeMerchantModal(): void {
    this.merchantModalOpen = false;
    this.editingMerchant = undefined;
  }

  openConditionCreate(): void {
    this.ensureMerchantOptions();
    this.conditionForm = this.emptyConditionForm();
    this.editingCondition = undefined;
    this.message = '';
    this.error = '';
    this.conditionModalOpen = true;
  }

  openConditionEdit(row: PosMerchantConditionFee): void {
    this.ensureMerchantOptions();
    this.conditionForm = {
      posMerchantId: row.posMerchantId ?? null,
      paymentType: row.paymentType ?? '',
      installmentType: row.installmentType ?? '',
      tax: row.tax ?? null,
      fee: row.fee ?? null,
      tna: row.tna ?? null,
      bankFee: row.bankFee ?? null
    };
    this.onConditionPaymentTypeChange();
    this.editingCondition = row;
    this.message = '';
    this.error = '';
    this.conditionModalOpen = true;
  }

  closeConditionModal(): void {
    this.conditionModalOpen = false;
    this.editingCondition = undefined;
  }

  openInstallmentCreate(): void {
    this.ensureInstallmentConditionFeeOptions();
    this.installmentForm = this.emptyInstallmentForm();
    this.editingInstallment = undefined;
    this.message = '';
    this.error = '';
    this.installmentModalOpen = true;
  }

  openInstallmentEdit(row: PosInstallments): void {
    this.ensureInstallmentConditionFeeOptions();
    this.installmentForm = {
      posMerchantConditionFeeId: row.posMerchantConditionFeeId ?? null,
      installmentFactor: row.installmentFactor ?? null,
      installmentNumber: row.installmentNumber ?? null,
      start: row.start ?? '',
      enable: row.enable ?? true
    };
    this.editingInstallment = row;
    this.message = '';
    this.error = '';
    this.installmentModalOpen = true;
  }

  closeInstallmentModal(): void {
    this.installmentModalOpen = false;
    this.editingInstallment = undefined;
  }

  openPlanCreate(): void {
    this.ensureInstallmentConditionFeeOptions();
    this.planForm = this.emptyPlanForm();
    this.planBinDraft = '';
    this.planDayDraft = '';
    this.editingPlan = undefined;
    this.message = '';
    this.error = '';
    this.planModalOpen = true;
  }

  openPlanEdit(row: PosInstallmentsPlan): void {
    this.ensureInstallmentConditionFeeOptions();
    this.planForm = {
      posMerchantConditionFeeId: row.posMerchantConditionFeeId ?? null,
      description: row.description ?? '',
      internacional: row.internacional ?? false,
      start: row.start ?? '',
      ending: row.ending ?? '',
      enabled: row.enabled ?? true,
      clientFinancialCharge: row.clientFinancialCharge ?? false,
      surchargePercentage: row.surchargePercentage ?? null,
      maxFinancingAmount: row.maxFinancingAmount ?? null,
      binList: row.binList ?? [],
      dayList: row.dayList ?? []
    };
    this.planBinDraft = '';
    this.planDayDraft = '';
    this.editingPlan = row;
    this.message = '';
    this.error = '';
    this.planModalOpen = true;
  }

  closePlanModal(): void {
    this.planModalOpen = false;
    this.editingPlan = undefined;
  }

  openPlanFactorCreate(): void {
    this.ensurePlanOptions();
    this.planFactorForm = this.emptyPlanFactorForm();
    this.editingPlanFactor = undefined;
    this.message = '';
    this.error = '';
    this.planFactorModalOpen = true;
  }

  openPlanFactorEdit(row: PosInstallmentsPlanFactor): void {
    this.ensurePlanOptions();
    this.planFactorForm = {
      posInstallmentsPlanId: row.posInstallmentsPlanId ?? null,
      installmentFactor: row.installmentFactor ?? null,
      installmentNumber: row.installmentNumber ?? null,
      displayText: row.displayText ?? '',
      enabled: row.enabled ?? true
    };
    this.editingPlanFactor = row;
    this.message = '';
    this.error = '';
    this.planFactorModalOpen = true;
  }

  closePlanFactorModal(): void {
    this.planFactorModalOpen = false;
    this.editingPlanFactor = undefined;
  }

  openAssignPlanCreate(): void {
    this.ensurePlanOptions();
    this.assignPlanForm = this.emptyAssignPlanForm();
    this.editingAssignPlan = undefined;
    this.message = '';
    this.error = '';
    this.assignPlanModalOpen = true;
  }

  openAssignPlanEdit(row: PosClientInstallmentsPlan): void {
    this.ensurePlanOptions();
    this.assignPlanForm = {
      clientId: row.clientId ?? null,
      posInstallmentsPlanId: row.posInstallmentsPlanId ?? null,
      enabled: row.enabled ?? true
    };
    this.editingAssignPlan = row;
    this.message = '';
    this.error = '';
    this.assignPlanModalOpen = true;
  }

  closeAssignPlanModal(): void {
    this.assignPlanModalOpen = false;
    this.editingAssignPlan = undefined;
  }

  openVirtualCreate(): void {
    this.loadAvailablePosTerminals();
    this.virtualForm = this.emptyVirtualForm();
    this.editingVirtual = undefined;
    this.message = '';
    this.error = '';
    this.virtualModalOpen = true;
  }

  openVirtualEdit(row: PosVirtualTerminal): void {
    this.loadAvailablePosTerminals();
    this.virtualForm = {
      posTerminalId: row.posTerminalId ?? null,
      terminalType: row.terminalType ?? (row.posTerminalId ? 'CP' : 'CNP'),
      clientId: row.clientId ?? null,
      status: row.status ?? 'ONLINE',
      enabled: row.enabled ?? true
    };
    this.editingVirtual = row;
    this.message = '';
    this.error = '';
    this.virtualModalOpen = true;
  }

  closeVirtualModal(): void {
    this.virtualModalOpen = false;
    this.editingVirtual = undefined;
  }

  onVirtualTerminalTypeChange(): void {
    if (this.virtualForm.terminalType === 'CNP') {
      this.virtualForm.posTerminalId = null;
    }
  }

  openVirtualMedioPagoCreate(): void {
    this.ensureVirtualTerminalOptions();
    this.virtualMedioPagoForm = this.emptyVirtualMedioPagoForm();
    this.editingVirtualMedioPago = undefined;
    this.message = '';
    this.error = '';
    this.virtualMedioPagoModalOpen = true;
  }

  openVirtualMedioPagoEdit(row: PosVirtualTerminalMedioPago): void {
    this.ensureVirtualTerminalOptions();
    this.virtualMedioPagoForm = {
      pos_virutal_terminal_id: row.pos_virutal_terminal_id ?? null,
      medio_pago_id: row.medio_pago_id ?? null
    };
    this.editingVirtualMedioPago = row;
    this.message = '';
    this.error = '';
    this.virtualMedioPagoModalOpen = true;
  }

  closeVirtualMedioPagoModal(): void {
    this.virtualMedioPagoModalOpen = false;
    this.editingVirtualMedioPago = undefined;
  }

  openTerminalPaywayCreate(): void {
    this.ensureMerchantOptions();
    this.ensureVirtualTerminalOptions();
    this.terminalPaywayForm = this.emptyTerminalPaywayForm();
    this.editingTerminalPayway = undefined;
    this.message = '';
    this.error = '';
    this.terminalPaywayModalOpen = true;
  }

  openTerminalPaywayEdit(row: PosTerminalPayway): void {
    this.ensureMerchantOptions();
    this.ensureVirtualTerminalOptions();
    this.terminalPaywayForm = {
      terminalId: row.terminalId ?? '',
      posVirtualTerminalId: row.posVirtualTerminalId ?? null,
      posMerchantId: row.posMerchantId ?? null
    };
    this.editingTerminalPayway = row;
    this.message = '';
    this.error = '';
    this.terminalPaywayModalOpen = true;
  }

  closeTerminalPaywayModal(): void {
    this.terminalPaywayModalOpen = false;
    this.editingTerminalPayway = undefined;
  }

  openTerminalGPCreate(): void {
    this.ensureVirtualTerminalOptions();
    this.ensureSubmerchantOptions();
    this.terminalGPForm = this.emptyTerminalGPForm();
    this.editingTerminalGP = undefined;
    this.message = '';
    this.error = '';
    this.terminalGPModalOpen = true;
  }

  openTerminalGPEdit(row: PosTerminalGP): void {
    this.ensureVirtualTerminalOptions();
    this.ensureSubmerchantOptions();
    this.terminalGPForm = {
      posVirtualTerminalId: row.posVirtualTerminalId ?? null,
      submerchantDataId: row.submerchantDataId ?? null
    };
    this.editingTerminalGP = row;
    this.message = '';
    this.error = '';
    this.terminalGPModalOpen = true;
  }

  closeTerminalGPModal(): void {
    this.terminalGPModalOpen = false;
    this.editingTerminalGP = undefined;
  }

  save(): void {
    if (!this.canSave) {
      return;
    }

    if (this.editing?.id) {
      this.update(this.editing.id);
      return;
    }

    this.create();
  }

  disable(row: PosTerminal): void {
    if (!row.id || !row.enabled) {
      return;
    }

    this.begin('delete');
    this.api
      .deletePosTerminal(row.id)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Terminal deshabilitada.';
          this.loadPosTerminals();
        },
        error: error => this.handleError(error, 'No se pudo deshabilitar la terminal.')
      });
  }

  saveMerchant(): void {
    if (!this.canSaveMerchant) {
      return;
    }

    if (this.editingMerchant?.id) {
      this.updateMerchant(this.editingMerchant.id);
      return;
    }

    this.createMerchant();
  }

  toggleMerchantEnabled(row: PosMerchantId): void {
    if (!row.id) {
      return;
    }

    const enabled = !(row.enabled ?? false);
    const request: PosMerchantIdSaveRequest = {
      clientId: row.clientId ?? null,
      merchantId: row.merchantId ?? '',
      enabled,
      descripcion: row.descripcion ?? '',
      mcc: row.mcc ?? '',
      cardBrand: row.cardBrand ?? 'VISA',
      paymentProcessType: row.paymentProcessType ?? 'PAYFAC',
      bankId: row.bankId ?? null,
      acquirer: row.acquirer ?? this.selectedAcquirer ?? 'PAYWAY'
    };

    this.begin('delete');
    this.api
      .updateMerchantId(row.id, request)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = `Merchant ID ${enabled ? 'habilitado' : 'deshabilitado'}.`;
          this.loadMerchantIds();
        },
        error: error => this.handleError(error, `No se pudo ${enabled ? 'habilitar' : 'deshabilitar'} el merchant ID.`)
      });
  }

  saveCondition(): void {
    this.onConditionPaymentTypeChange();
    if (!this.canSaveCondition) {
      return;
    }

    this.error = '';
    const duplicateSearch = this.conditionFeeDuplicateSearch();
    if (!duplicateSearch) {
      this.submitCondition();
      return;
    }

    this.api.getConditionFees(0, 2, duplicateSearch).subscribe({
      next: response => {
        const duplicate = (response.content ?? []).some(row => row.id !== this.editingCondition?.id);
        if (duplicate) {
          this.error = this.conditionFeeDuplicateMessage();
          this.cdr.markForCheck();
          return;
        }
        this.submitCondition();
      },
      error: error => this.handleError(error, 'No se pudo validar el merchant condition fee.')
    });
  }

  deleteCondition(row: PosMerchantConditionFee): void {
    if (!row.id) {
      return;
    }

    this.begin('delete');
    this.api
      .deleteConditionFee(row.id)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Merchant condition fee eliminado.';
          this.loadConditionFees();
        },
        error: error => this.handleError(error, 'No se pudo eliminar el merchant condition fee.')
      });
  }

  saveInstallment(): void {
    if (!this.canSaveInstallment) {
      return;
    }

    this.error = '';
    this.api.getInstallments(0, 2, this.installmentDuplicateSearch()).subscribe({
      next: response => {
        const duplicate = (response.content ?? []).some(row => row.id !== this.editingInstallment?.id);
        if (duplicate) {
          this.error = this.installmentDuplicateMessage();
          this.cdr.markForCheck();
          return;
        }
        this.submitInstallment();
      },
      error: error => this.handleError(error, 'No se pudo validar el installment.')
    });
  }

  private submitInstallment(): void {
    if (this.editingInstallment?.id) {
      this.updateInstallment(this.editingInstallment.id);
      return;
    }

    this.createInstallment();
  }

  private installmentDuplicateSearch(): string {
    return `posMerchantConditionFeeId==${this.installmentForm.posMerchantConditionFeeId};installmentNumber==${this.installmentForm.installmentNumber}`;
  }

  private installmentDuplicateMessage(): string {
    return `Ya existe un registro de ${this.installmentForm.installmentNumber} cuotas para el merchant condition fee seleccionado.`;
  }

  deleteInstallment(row: PosInstallments): void {
    if (!row.id) {
      return;
    }

    this.begin('delete');
    this.api
      .deleteInstallment(row.id)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Installment deshabilitado.';
          this.loadInstallments();
        },
        error: error => this.handleError(error, 'No se pudo deshabilitar el installment.')
      });
  }

  savePlan(): void {
    if (!this.canSavePlan) {
      return;
    }

    if (this.editingPlan?.id) {
      this.updatePlan(this.editingPlan.id);
      return;
    }

    this.createPlan();
  }

  deletePlan(row: PosInstallmentsPlan): void {
    if (!row.id || !row.enabled) {
      return;
    }

    this.begin('delete');
    this.api
      .deleteInstallmentsPlan(row.id)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Plan deshabilitado.';
          this.loadPlans();
        },
        error: error => this.handleError(error, 'No se pudo deshabilitar el plan.')
      });
  }

  savePlanFactor(): void {
    if (!this.canSavePlanFactor) {
      return;
    }

    this.error = '';
    this.api.getInstallmentsPlanFactors(0, 500, `installmentNumber==${this.planFactorForm.installmentNumber}`).subscribe({
      next: response => {
        const duplicateMessage = this.planFactorDuplicateMessage(response.content ?? []);
        if (duplicateMessage) {
          this.error = duplicateMessage;
          this.cdr.markForCheck();
          return;
        }
        this.submitPlanFactor();
      },
      error: error => this.handleError(error, 'No se pudo validar el installment de plan.')
    });
  }

  private submitPlanFactor(): void {
    if (this.editingPlanFactor?.id) {
      this.updatePlanFactor(this.editingPlanFactor.id);
      return;
    }

    this.createPlanFactor();
  }

  private planFactorDuplicateMessage(factors: PosInstallmentsPlanFactor[]): string {
    const selectedPlan = this.planOptions.find(plan => plan.id === this.planFactorForm.posInstallmentsPlanId);
    if (!selectedPlan) {
      return '';
    }
    const duplicate = factors.find(factor => factor.id !== this.editingPlanFactor?.id && this.conflictsWithPlanFactor(factor, selectedPlan));
    if (!duplicate) {
      return '';
    }
    if (duplicate.posInstallmentsPlanId === selectedPlan.id) {
      return `Ya existe un registro de ${this.planFactorForm.installmentNumber} cuotas para el plan seleccionado.`;
    }
    return `Ya existe otro plan con vigencia solapada para el mismo merchant condition fee y ${this.planFactorForm.installmentNumber} cuotas.`;
  }

  private conflictsWithPlanFactor(factor: PosInstallmentsPlanFactor, selectedPlan: PosInstallmentsPlan): boolean {
    if (factor.posInstallmentsPlanId === selectedPlan.id) {
      return true;
    }
    const factorPlan = this.planOptions.find(plan => plan.id === factor.posInstallmentsPlanId);
    return Boolean(
      factorPlan &&
        factorPlan.posMerchantConditionFeeId === selectedPlan.posMerchantConditionFeeId &&
        this.planDatesOverlap(selectedPlan, factorPlan)
    );
  }

  private planDatesOverlap(first: PosInstallmentsPlan, second: PosInstallmentsPlan): boolean {
    if (!first.start || !first.ending || !second.start || !second.ending) {
      return false;
    }
    return first.start <= second.ending && second.start <= first.ending;
  }

  deletePlanFactor(row: PosInstallmentsPlanFactor): void {
    if (!row.id || !row.enabled) {
      return;
    }

    this.begin('delete');
    this.api
      .deleteInstallmentsPlanFactor(row.id)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Installment de plan deshabilitado.';
          this.loadPlanFactors();
        },
        error: error => this.handleError(error, 'No se pudo deshabilitar el installment de plan.')
      });
  }

  saveAssignPlan(): void {
    if (!this.canSaveAssignPlan) {
      return;
    }

    this.error = '';
    this.api.getClientInstallmentsPlans(0, 2, this.assignPlanDuplicateSearch()).subscribe({
      next: response => {
        const duplicate = (response.content ?? []).some(row => row.id !== this.editingAssignPlan?.id);
        if (duplicate) {
          this.error = this.assignPlanDuplicateMessage();
          this.cdr.markForCheck();
          return;
        }
        this.submitAssignPlan();
      },
      error: error => this.handleError(error, 'No se pudo validar la asignacion de plan.')
    });
  }

  private submitAssignPlan(): void {
    if (this.editingAssignPlan?.id) {
      this.updateAssignPlan(this.editingAssignPlan.id);
      return;
    }

    this.createAssignPlan();
  }

  private assignPlanDuplicateSearch(): string {
    return `clientId==${this.assignPlanForm.clientId};posInstallmentsPlanId==${this.assignPlanForm.posInstallmentsPlanId}`;
  }

  private assignPlanDuplicateMessage(): string {
    return `El cliente ${this.assignPlanForm.clientId} ya tiene asignado el plan ${this.assignPlanForm.posInstallmentsPlanId}.`;
  }

  deleteAssignPlan(row: PosClientInstallmentsPlan): void {
    if (!row.id || !row.enabled) {
      return;
    }

    this.begin('delete');
    this.api
      .deleteClientInstallmentsPlan(row.id)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Asignacion de plan deshabilitada.';
          this.loadAssignPlans();
        },
        error: error => this.handleError(error, 'No se pudo deshabilitar la asignacion de plan.')
      });
  }

  saveVirtual(): void {
    if (!this.canSaveVirtual) {
      return;
    }

    if (this.editingVirtual?.id) {
      this.updateVirtual(this.editingVirtual.id);
      return;
    }

    this.createVirtual();
  }

  deleteVirtual(row: PosVirtualTerminal): void {
    if (!row.id) {
      return;
    }

    this.begin('delete');
    this.api
      .deleteVirtualTerminal(row.id)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Terminal virtual eliminada.';
          this.loadVirtualTerminals();
        },
        error: error => this.handleError(error, 'No se pudo eliminar la terminal virtual.')
      });
  }

  saveVirtualMedioPago(): void {
    if (!this.canSaveVirtualMedioPago) {
      return;
    }

    if (this.editingVirtualMedioPago?.id) {
      this.updateVirtualMedioPago(this.editingVirtualMedioPago.id);
      return;
    }

    this.createVirtualMedioPago();
  }

  deleteVirtualMedioPago(row: PosVirtualTerminalMedioPago): void {
    if (!row.id) {
      return;
    }

    this.begin('delete');
    this.api
      .deleteVirtualTerminalMedioPago(row.id)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Medio de pago eliminado de la terminal virtual.';
          this.loadVirtualTerminalMedioPagos();
        },
        error: error => this.handleError(error, 'No se pudo eliminar el medio de pago.')
      });
  }

  saveTerminalPayway(): void {
    if (!this.canSaveTerminalPayway) {
      return;
    }

    if (this.editingTerminalPayway?.id) {
      this.updateTerminalPayway(this.editingTerminalPayway.id);
      return;
    }

    this.createTerminalPayway();
  }

  deleteTerminalPayway(row: PosTerminalPayway): void {
    if (!row.id) {
      return;
    }

    this.begin('delete');
    this.api
      .deleteTerminalPayway(row.id)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Terminal Payway eliminada.';
          this.loadTerminalPayways();
        },
        error: error => this.handleError(error, 'No se pudo eliminar la terminal Payway.')
      });
  }

  saveTerminalGP(): void {
    if (!this.canSaveTerminalGP) return;
    if (this.editingTerminalGP?.id) {
      this.updateTerminalGP(this.editingTerminalGP.id);
      return;
    }
    this.createTerminalGP();
  }

  removeTerminalGPSubmerchant(row: PosTerminalGP): void {
    if (!row.id || !row.submerchantDataId) return;
    this.begin('update');
    this.api.removeTerminalGPSubmerchant(row.id)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Submerchant desadherido de la Terminal Global Processing.';
          this.loadTerminalGPs();
        },
        error: error => this.handleError(error, 'No se pudo desadherir el submerchant.')
      });
  }

  deleteTerminalGP(row: PosTerminalGP): void {
    if (!row.id) return;
    this.begin('delete');
    this.api.deleteTerminalGP(row.id)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Terminal Global Processing eliminada.';
          this.loadTerminalGPs();
        },
        error: error => this.handleError(error, 'No se pudo eliminar la Terminal Global Processing.')
      });
  }

  loadPosTerminals(): void {
    this.tableLoading = true;
    this.api
      .getPosTerminals({ page: this.page, size: this.size, search: this.searchText, filters: this.cleanFilters() })
      .pipe(
        finalize(() => {
          this.tableLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.rows = response.data ?? [];
          this.total = response.recordsFiltered ?? response.totalElements ?? 0;
          this.totalRecords = response.recordsTotal ?? this.total;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar el listado de terminales.')
      });
  }

  loadMerchantIds(): void {
    this.tableLoading = true;
    this.api
      .getMerchantIds(this.merchantPage, this.merchantSize, this.buildMerchantRsql())
      .pipe(
        finalize(() => {
          this.tableLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.merchantRows = response.content ?? [];
          this.merchantTotal = response.totalElements ?? 0;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar el listado de merchant ID.')
      });
  }

  loadConditionFees(): void {
    this.tableLoading = true;
    this.api
      .getConditionFees(this.conditionPage, this.conditionSize, this.buildConditionRsql())
      .pipe(
        finalize(() => {
          this.tableLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.conditionRows = response.content ?? [];
          this.conditionTotal = response.totalElements ?? 0;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar el listado de merchant condition fee.')
      });
  }

  loadInstallments(): void {
    this.tableLoading = true;
    this.api
      .getInstallments(this.installmentPage, this.installmentSize, this.buildInstallmentRsql())
      .pipe(
        finalize(() => {
          this.tableLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.installmentRows = response.content ?? [];
          this.installmentTotal = response.totalElements ?? 0;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar el listado de installments.')
      });
  }

  loadPlans(): void {
    this.tableLoading = true;
    this.api
      .getInstallmentsPlans(this.planPage, this.planSize, this.buildPlanRsql())
      .pipe(
        finalize(() => {
          this.tableLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.planRows = response.content ?? [];
          this.planTotal = response.totalElements ?? 0;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar el listado de planes.')
      });
  }

  loadPlanFactors(): void {
    this.tableLoading = true;
    this.api
      .getInstallmentsPlanFactors(this.planFactorPage, this.planFactorSize, this.buildPlanFactorRsql())
      .pipe(
        finalize(() => {
          this.tableLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.planFactorRows = response.content ?? [];
          this.planFactorTotal = response.totalElements ?? 0;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar el listado de installments de planes.')
      });
  }

  loadAssignPlans(): void {
    this.tableLoading = true;
    this.api
      .getClientInstallmentsPlans(this.assignPlanPage, this.assignPlanSize, this.buildAssignPlanRsql())
      .pipe(
        finalize(() => {
          this.tableLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.assignPlanRows = response.content ?? [];
          this.assignPlanTotal = response.totalElements ?? 0;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar el listado de asignaciones de plan.')
      });
  }

  loadVirtualTerminals(): void {
    this.tableLoading = true;
    this.api
      .getVirtualTerminals(this.virtualPage, this.virtualSize, this.buildVirtualRsql())
      .pipe(
        finalize(() => {
          this.tableLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.virtualRows = response.content ?? [];
          this.virtualTotal = response.totalElements ?? 0;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar el listado de terminales virtuales.')
      });
  }

  loadVirtualTerminalMedioPagos(): void {
    this.tableLoading = true;
    this.api
      .getVirtualTerminalMedioPagos(this.virtualMedioPagoPage, this.virtualMedioPagoSize, this.buildVirtualMedioPagoRsql())
      .pipe(
        finalize(() => {
          this.tableLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.virtualMedioPagoRows = response.content ?? [];
          this.virtualMedioPagoTotal = response.totalElements ?? 0;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar el listado de medios de pago por terminal virtual.')
      });
  }

  loadTerminalPayways(): void {
    this.tableLoading = true;
    this.api
      .getTerminalPayways(this.terminalPaywayPage, this.terminalPaywaySize, this.buildTerminalPaywayRsql())
      .pipe(
        finalize(() => {
          this.tableLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.terminalPaywayRows = response.content ?? [];
          this.terminalPaywayTotal = response.totalElements ?? 0;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar el listado de Terminal Payway.')
      });
  }

  loadTerminalGPs(): void {
    this.tableLoading = true;
    this.api.getTerminalGPs(this.terminalGPPage, this.terminalGPSize, this.buildTerminalGPRsql())
      .pipe(finalize(() => {
        this.tableLoading = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: response => {
          this.terminalGPRows = response.content ?? [];
          this.terminalGPTotal = response.totalElements ?? 0;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar el listado de Terminal Global Processing.')
      });
  }

  loadConfigurations(): void {
    this.tableLoading = true;
    this.api
      .getTerminalConfigurations(this.configurationPage, this.configurationSize, this.buildConfigurationRsql())
      .pipe(
        finalize(() => {
          this.tableLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.configurationRows = response.content ?? [];
          this.configurationTotal = response.totalElements ?? 0;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar la configuracion de terminales.')
      });
  }

  loadSubmerchants(): void {
    this.tableLoading = true;
    this.api.getSubmerchants(this.submerchantPage, this.submerchantSize)
      .pipe(finalize(() => { this.tableLoading = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: response => {
          this.submerchantRows = response.content ?? [];
          this.submerchantTotal = response.totalElements ?? 0;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar el listado de submerchants.')
      });
  }

  changeSubmerchantPage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.submerchantTotalPages || nextPage === this.submerchantPage) return;
    this.submerchantPage = nextPage;
    this.loadSubmerchants();
  }

  openSubmerchantCreate(): void {
    this.submerchantClientRequest?.unsubscribe();
    this.submerchantClientError = '';
    this.submerchantClientNotice = '';
    this.editingSubmerchant = undefined;
    this.submerchantForm = this.emptySubmerchantForm();
    this.selectedProvinceId = null;
    this.cities = [];
    this.loadProvinces();
    this.submerchantModalOpen = true;
  }

  openSubmerchantEdit(row: SubmerchantSummary): void {
    if (!row.id) return;
    this.submerchantClientRequest?.unsubscribe();
    this.submerchantClientLoading = false;
    this.submerchantClientError = '';
    this.submerchantClientNotice = '';
    this.cities = [];
    this.selectedProvinceId = null;
    this.editingSubmerchant = row;
    this.loadProvinces();
    this.api.getSubmerchant(row.id).subscribe({
      next: data => {
        this.submerchantForm = {
          ...this.emptySubmerchantForm(),
          clientId: data.clientId ?? null,
          idLocalidad: data.idLocalidad ?? null,
          cnp: data.cnp ?? row.cardNotPresent ?? false,
          submerchantDTO: { ...this.emptySubmerchantForm().submerchantDTO, ...data, subcomercioId: row.submerchantId }
        };
        const birthDate = data.fechaNacimientoTitular;
        if (birthDate && /^\d{2}\/\d{2}\/\d{4}$/.test(birthDate)) {
          this.submerchantForm.submerchantDTO.fechaNacimientoTitular = birthDate.split('/').reverse().join('-');
        }
        this.selectedProvinceId = data.provinceId ?? null;
        if (this.selectedProvinceId) {
          this.api.getCities(this.selectedProvinceId).subscribe({
            next: cities => { this.cities = cities ?? []; this.cdr.markForCheck(); },
            error: error => this.handleError(error, 'No se pudieron cargar las localidades.')
          });
        }
        this.submerchantModalOpen = true;
        this.cdr.markForCheck();
      },
      error: error => this.handleError(error, 'No se pudo cargar el submerchant.')
    });
  }

  closeSubmerchantModal(): void {
    if (this.submerchantSaving) return;
    this.submerchantClientRequest?.unsubscribe();
    this.submerchantModalOpen = false;
    this.editingSubmerchant = undefined;
  }

  onSubmerchantClientChange(clientId: number | null): void {
    if (this.editingSubmerchant) return;
    this.submerchantClientRequest?.unsubscribe();
    this.submerchantForm.clientId = clientId;
    this.submerchantClientError = '';
    this.submerchantClientNotice = '';
    this.applySubmerchantClient(null);
    if (!clientId) return;
    this.submerchantClientLoading = true;
    this.submerchantClientRequest = this.clientOptionsApi.getById(clientId).pipe(
      switchMap(client => {
        this.applySubmerchantClient(client);
        return this.api.getProvinces().pipe(map(provinces => ({ client, provinces })));
      }),
      switchMap(({ client, provinces }) => {
        this.provinces = provinces;
        const province = provinces.find(item => item.code.toUpperCase() === client.provincia_codigo?.toUpperCase());
        this.selectedProvinceId = province?.id ?? null;
        return (province ? this.api.getCities(province.id) : of([] as Locality[])).pipe(
          map(cities => ({ client, cities })));
      }),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => { this.submerchantClientLoading = false; this.cdr.markForCheck(); })
    ).subscribe({
      next: ({ client, cities }) => {
        this.cities = cities;
        const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
        const matches = cities.filter(city => normalize(city.name) === normalize(client.localidad ?? ''));
        const city = matches.length === 1 ? matches[0] : undefined;
        this.submerchantForm.idLocalidad = city?.id ?? null;
        if (city) {
          this.submerchantForm.submerchantDTO.localidad = city.name.slice(0, 20);
          this.submerchantForm.submerchantDTO.provincia = city.provinceCode;
        } else {
          this.submerchantClientNotice = 'Seleccioná provincia y localidad: no se encontró una coincidencia única para el cliente.';
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.submerchantClientError = 'No se pudieron completar los datos del cliente. Reintentá la consulta.';
        this.cdr.markForCheck();
      }
    });
  }

  onSubmerchantNameInput(field: 'razonSocial' | 'nombreComercial', event: Event): void {
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const value = input.value.toUpperCase().slice(0, 20);
    this.submerchantForm.submerchantDTO[field] = value;
    input.value = value;
    input.setSelectionRange(start, end);
  }

  private applySubmerchantClient(client: ClientDetail | null): void {
    const numberValue = (value: string | null | undefined): number | null => {
      const digits = (value ?? '').replace(/[\s()+.-]/g, '');
      if (!/^\d+$/.test(digits)) return null;
      const number = Number(digits);
      return Number.isSafeInteger(number) ? number : null;
    };
    Object.assign(this.submerchantForm.submerchantDTO, {
      razonSocial: (client?.nombre_legal ?? '').toUpperCase().slice(0, 20),
      nombreComercial: (client?.razon_social ?? '').toUpperCase().slice(0, 20),
      mcc: numberValue(client?.mcc),
      cuit: numberValue(client?.documento),
      calleNumero: [client?.domicilio, client?.domicilio_nro].filter(Boolean).join(' '),
      codPostal: (client?.cp ?? '').toUpperCase(),
      mailComercio: client?.email ?? '',
      telefono: numberValue(client?.telefono),
      urlSitioWeb: client?.web ?? '',
      localidad: client?.localidad ?? '',
      provincia: client?.provincia_codigo ?? ''
    });
    this.selectedProvinceId = null;
    this.cities = [];
    this.submerchantForm.idLocalidad = null;
  }

  loadProvinces(): void {
    if (this.provinces.length) return;
    this.api.getProvinces().subscribe({
      next: provinces => { this.provinces = provinces ?? []; this.cdr.markForCheck(); },
      error: error => this.handleError(error, 'No se pudieron cargar las provincias.')
    });
  }

  onProvinceChange(): void {
    this.cities = [];
    this.submerchantForm.idLocalidad = null;
	this.submerchantForm.submerchantDTO.localidad = '';
	this.submerchantForm.submerchantDTO.codPostal = '';
    const province = this.provinces.find(item => item.id === Number(this.selectedProvinceId));
    this.submerchantForm.submerchantDTO.provincia = province?.code ?? '';
    if (!province) return;
    this.api.getCities(province.id).subscribe({
      next: cities => {
		this.cities = cities ?? [];
        this.cdr.markForCheck();
      },
      error: error => this.handleError(error, 'No se pudieron cargar las localidades.')
    });
  }

  onCityChange(): void {
    const city = this.cities.find(item => item.id === Number(this.submerchantForm.idLocalidad));
	this.submerchantForm.submerchantDTO.localidad = city?.name.slice(0, 20) ?? '';
	this.submerchantForm.submerchantDTO.codPostal = city
	  ? `${city.provinceCode}${city.postalCode}`.toUpperCase()
	  : '';
	if (city?.provinceCode) this.submerchantForm.submerchantDTO.provincia = city.provinceCode;
  }

  onCpaChange(value: string): void {
	this.submerchantForm.submerchantDTO.codPostal = (value ?? '').toUpperCase();
  }

  saveSubmerchant(): void {
    if (this.submerchantSaving || this.loadingAction || !this.canSaveSubmerchant) return;
    this.submerchantSaving = true;
    this.submerchantForm.submerchantDTO.subcomercioId = this.editingSubmerchant?.submerchantId;
    this.begin(this.editingSubmerchant ? 'update' : 'create');
    this.api.saveSubmerchant(this.submerchantForm)
      .pipe(finalize(() => {
        this.submerchantSaving = false;
        this.finish();
      }))
      .subscribe({
        next: response => {
          this.message = `Submerchant ${response.subcomercioId ?? ''} guardado y Merchant ID GP-${response.subcomercioId ?? ''} generado.`;
          this.submerchantSaving = false;
          this.closeSubmerchantModal();
          this.loadSubmerchants();
        },
        error: error => this.handleError(error, 'No se pudo guardar el submerchant.')
      });
  }

  openConfigurationDetail(row: TerminalConfigurationSummary): void {
    if (!row.serialNumber) {
      return;
    }
    this.configurationDetailRow = row;
    this.configurationDetail = undefined;
    this.configurationDetailLoading = true;
    this.api
      .getTerminalConfigurationDetail(row.serialNumber)
      .pipe(
        finalize(() => {
          this.configurationDetailLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.configurationDetail = response;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar el detalle de configuracion.')
      });
  }

  closeConfigurationDetail(): void {
    this.configurationDetail = undefined;
    this.configurationDetailRow = undefined;
    this.configurationDetailLoading = false;
  }

  applyFilters(): void {
    this.page = 0;
    this.loadPosTerminals();
  }

  applyMerchantFilters(): void {
    this.merchantPage = 0;
    this.loadMerchantIds();
  }

  applyConditionFilters(): void {
    this.conditionPage = 0;
    this.loadConditionFees();
  }

  applyInstallmentFilters(): void {
    this.installmentPage = 0;
    this.loadInstallments();
  }

  applyPlanFilters(): void {
    this.planPage = 0;
    this.loadPlans();
  }

  applyPlanFactorFilters(): void {
    this.planFactorPage = 0;
    this.loadPlanFactors();
  }

  applyAssignPlanFilters(): void {
    this.assignPlanPage = 0;
    this.loadAssignPlans();
  }

  applyVirtualFilters(): void {
    this.virtualPage = 0;
    this.loadVirtualTerminals();
  }

  applyVirtualMedioPagoFilters(): void {
    this.virtualMedioPagoPage = 0;
    this.loadVirtualTerminalMedioPagos();
  }

  applyTerminalPaywayFilters(): void {
    this.terminalPaywayPage = 0;
    this.loadTerminalPayways();
  }

  applyTerminalGPFilters(): void {
    this.terminalGPPage = 0;
    this.loadTerminalGPs();
  }

  applyConfigurationFilters(): void {
    this.configurationPage = 0;
    this.loadConfigurations();
  }

  clearFilters(): void {
    this.filters = {};
    this.searchText = '';
    this.page = 0;
    this.loadPosTerminals();
  }

  clearMerchantFilters(): void {
    this.merchantFilters = {};
    this.merchantSearchText = '';
    this.merchantPage = 0;
    this.loadMerchantIds();
  }

  clearConditionFilters(): void {
    this.conditionFilters = {};
    this.conditionSearchText = '';
    this.conditionPage = 0;
    this.loadConditionFees();
  }

  clearInstallmentFilters(): void {
    this.installmentFilters = {};
    this.installmentSearchText = '';
    this.installmentPage = 0;
    this.loadInstallments();
  }

  clearPlanFilters(): void {
    this.planFilters = {};
    this.planSearchText = '';
    this.planPage = 0;
    this.loadPlans();
  }

  clearPlanFactorFilters(): void {
    this.planFactorFilters = {};
    this.planFactorSearchText = '';
    this.planFactorPage = 0;
    this.loadPlanFactors();
  }

  clearAssignPlanFilters(): void {
    this.assignPlanFilters = {};
    this.assignPlanSearchText = '';
    this.assignPlanPage = 0;
    this.loadAssignPlans();
  }

  clearVirtualFilters(): void {
    this.virtualFilters = {};
    this.virtualSearchText = '';
    this.virtualPage = 0;
    this.loadVirtualTerminals();
  }

  clearVirtualMedioPagoFilters(): void {
    this.virtualMedioPagoFilters = {};
    this.virtualMedioPagoSearchText = '';
    this.virtualMedioPagoPage = 0;
    this.loadVirtualTerminalMedioPagos();
  }

  clearTerminalPaywayFilters(): void {
    this.terminalPaywayFilters = {};
    this.terminalPaywaySearchText = '';
    this.terminalPaywayPage = 0;
    this.loadTerminalPayways();
  }

  clearTerminalGPFilters(): void {
    this.terminalGPFilters = {};
    this.terminalGPSearchText = '';
    this.terminalGPPage = 0;
    this.loadTerminalGPs();
  }

  clearConfigurationFilters(): void {
    this.configurationFilters = {};
    this.configurationSearchText = '';
    this.configurationPage = 0;
    this.loadConfigurations();
  }

  changePage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.totalPages || nextPage === this.page) {
      return;
    }
    this.page = nextPage;
    this.loadPosTerminals();
  }

  changeSize(): void {
    this.page = 0;
    this.loadPosTerminals();
  }

  changeMerchantPage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.merchantTotalPages || nextPage === this.merchantPage) {
      return;
    }
    this.merchantPage = nextPage;
    this.loadMerchantIds();
  }

  changeMerchantSize(): void {
    this.merchantPage = 0;
    this.loadMerchantIds();
  }

  changeConditionPage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.conditionTotalPages || nextPage === this.conditionPage) {
      return;
    }
    this.conditionPage = nextPage;
    this.loadConditionFees();
  }

  changeConditionSize(): void {
    this.conditionPage = 0;
    this.loadConditionFees();
  }

  changeInstallmentPage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.installmentTotalPages || nextPage === this.installmentPage) {
      return;
    }
    this.installmentPage = nextPage;
    this.loadInstallments();
  }

  changeInstallmentSize(): void {
    this.installmentPage = 0;
    this.loadInstallments();
  }

  changePlanPage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.planTotalPages || nextPage === this.planPage) {
      return;
    }
    this.planPage = nextPage;
    this.loadPlans();
  }

  changePlanSize(): void {
    this.planPage = 0;
    this.loadPlans();
  }

  changePlanFactorPage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.planFactorTotalPages || nextPage === this.planFactorPage) {
      return;
    }
    this.planFactorPage = nextPage;
    this.loadPlanFactors();
  }

  changePlanFactorSize(): void {
    this.planFactorPage = 0;
    this.loadPlanFactors();
  }

  changeAssignPlanPage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.assignPlanTotalPages || nextPage === this.assignPlanPage) {
      return;
    }
    this.assignPlanPage = nextPage;
    this.loadAssignPlans();
  }

  changeAssignPlanSize(): void {
    this.assignPlanPage = 0;
    this.loadAssignPlans();
  }

  changeVirtualPage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.virtualTotalPages || nextPage === this.virtualPage) {
      return;
    }
    this.virtualPage = nextPage;
    this.loadVirtualTerminals();
  }

  changeVirtualSize(): void {
    this.virtualPage = 0;
    this.loadVirtualTerminals();
  }

  changeVirtualMedioPagoPage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.virtualMedioPagoTotalPages || nextPage === this.virtualMedioPagoPage) {
      return;
    }
    this.virtualMedioPagoPage = nextPage;
    this.loadVirtualTerminalMedioPagos();
  }

  changeVirtualMedioPagoSize(): void {
    this.virtualMedioPagoPage = 0;
    this.loadVirtualTerminalMedioPagos();
  }

  changeTerminalPaywayPage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.terminalPaywayTotalPages || nextPage === this.terminalPaywayPage) {
      return;
    }
    this.terminalPaywayPage = nextPage;
    this.loadTerminalPayways();
  }

  changeTerminalPaywaySize(): void {
    this.terminalPaywayPage = 0;
    this.loadTerminalPayways();
  }

  changeTerminalGPPage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.terminalGPTotalPages || nextPage === this.terminalGPPage) return;
    this.terminalGPPage = nextPage;
    this.loadTerminalGPs();
  }

  changeTerminalGPSize(): void {
    this.terminalGPPage = 0;
    this.loadTerminalGPs();
  }

  changeConfigurationPage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.configurationTotalPages || nextPage === this.configurationPage) {
      return;
    }
    this.configurationPage = nextPage;
    this.loadConfigurations();
  }

  changeConfigurationSize(): void {
    this.configurationPage = 0;
    this.loadConfigurations();
  }

  merchantOptionLabel(id?: number | null): string {
    if (!id) {
      return '-';
    }
    const merchant = this.merchantOptions.find(row => row.id === id) ?? this.merchantRows.find(row => row.id === id);
    if (!merchant) {
      return String(id);
    }
    return `${merchant.merchantId ?? '-'} - ${this.logicalCardBrand(merchant.cardBrand)} - ${merchant.id ?? '-'}`;
  }

  posTerminalOptionLabel(id?: number | null): string {
    if (!id) {
      return '-';
    }
    const terminal = this.availablePosTerminals.find(row => row.id === id) ?? this.rows.find(row => row.id === id);
    if (!terminal) {
      return String(id);
    }
    return `${this.terminalCode(terminal.id)} - ${terminal.serialNumber ?? 'Sin serial'}`;
  }

  virtualTerminalOptionLabel(id?: number | null): string {
    if (!id) {
      return '-';
    }
    const terminal = this.virtualTerminalOptions.find(row => row.id === id) ?? this.virtualRows.find(row => row.id === id);
    if (!terminal) {
      return String(id);
    }
    const posTerminal = this.availablePosTerminals.find(row => row.id === terminal.posTerminalId)
      ?? this.rows.find(row => row.id === terminal.posTerminalId);
    const serialNumber = terminal.posTerminalId == null ? undefined
      : this.virtualPosTerminalSerials.get(terminal.posTerminalId) ?? posTerminal?.serialNumber;
    return `${terminal.terminalId ?? id} - ${serialNumber?.trim() || 'Sin serial'}`;
  }

  submerchantOptionLabel(id?: number | null): string {
    if (!id) return '-';
    const submerchant = this.submerchantOptions.find(row => row.id === id) ?? this.submerchantRows.find(row => row.id === id);
    return submerchant ? `${submerchant.submerchantId ?? '-'} - ${submerchant.nombreComercial ?? '-'} - ${id}` : String(id);
  }

  conditionFeeOptionLabel(id?: number | null): string {
    if (!id) {
      return '-';
    }
    const condition =
      this.installmentConditionFeeOptions.find(row => row.id === id) ??
      this.conditionRows.find(row => row.id === id);
    if (!condition) {
      return String(id);
    }
    return `#${condition.id} - ${this.merchantOptionLabel(condition.posMerchantId)} - ${condition.paymentType ?? '-'}/${condition.installmentType ?? '-'}`;
  }

  conditionFeeMerchantLabel(condition: PosMerchantConditionFee): string {
    const merchant = this.merchantOptions.find(row => row.id === condition.posMerchantId) ??
      this.merchantRows.find(row => row.id === condition.posMerchantId);
    if (!merchant) {
      return `${condition.posMerchantId ?? '-'} - - - ${condition.id ?? '-'}`;
    }
    return `${merchant.merchantId ?? '-'} - ${this.logicalCardBrand(merchant.cardBrand)} - ${condition.id ?? '-'}`;
  }

  planOptionLabel(id?: number | null): string {
    if (!id) {
      return '-';
    }
    const plan = this.planOptions.find(row => row.id === id) ?? this.planRows.find(row => row.id === id);
    if (!plan) {
      return String(id);
    }
    return `#${plan.id} - ${plan.description ?? 'Sin descripcion'}`;
  }

  listLabel(values?: string[]): string {
    return values?.length ? values.join(', ') : '-';
  }

  logicalCardBrand(brand?: string | null): string {
    if (!brand) {
      return '-';
    }
    const normalized = brand.toUpperCase().trim();
    if (normalized === 'MAESTRO') {
      return 'MASTERCARD';
    }
    if (normalized === 'VISA' || normalized.startsWith('VISA_')) {
      return 'VISA';
    }
    return normalized;
  }

  configurationValue(key: string): unknown {
    return this.configurationDetail ? this.configurationDetail[key] : undefined;
  }

  configurationVirtualTerminals(): unknown[] {
    const terminals = this.configurationValue('virtualTerminal');
    return Array.isArray(terminals) ? terminals : this.configurationDetail ? [this.configurationDetail] : [];
  }

  detailArray(key: string): unknown[] {
    const value = this.configurationValue(key);
    return Array.isArray(value) ? value : [];
  }

  detailArrayFirst(...keys: string[]): unknown[] {
    for (const key of keys) {
      const value = this.detailArray(key);
      if (value.length) {
        return value;
      }
    }
    return [];
  }

  detailObjectValue(source: unknown, key: string): unknown {
    if (!source || typeof source !== 'object') {
      return undefined;
    }
    return (source as Record<string, unknown>)[key];
  }

  detailChildArray(source: unknown, key: string): unknown[] {
    const value = this.detailObjectValue(source, key);
    return Array.isArray(value) ? value : [];
  }

  merchantBrandLabel(merchant: unknown): string {
    const brands = this.detailChildArray(merchant, 'merchantBrandData')
      .map(item => this.detailObjectValue(item, 'cardBrand'))
      .filter((value): value is string => typeof value === 'string' && value.length > 0);
    return brands.length ? brands.join(', ') : '-';
  }

  merchantBrandValue(merchant: unknown): string {
    const brand = this.detailObjectValue(merchant, 'brand') ?? this.detailObjectValue(merchant, 'cardBrand');
    return this.detailText(brand);
  }

  detailText(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    if (typeof value === 'boolean') {
      return value ? 'Si' : 'No';
    }
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : '-';
    }
    return String(value);
  }

  addPlanBin(): void {
    const value = this.planBinDraft.trim();
    if (!value || this.planForm.binList.includes(value)) {
      this.planBinDraft = '';
      return;
    }
    this.planForm.binList = [...this.planForm.binList, value];
    this.planBinDraft = '';
  }

  removePlanBin(value: string): void {
    this.planForm.binList = this.planForm.binList.filter(item => item !== value);
  }

  addPlanDay(): void {
    const value = this.planDayDraft.trim();
    if (!value || this.planForm.dayList.includes(value)) {
      this.planDayDraft = '';
      return;
    }
    this.planForm.dayList = [...this.planForm.dayList, value];
    this.planDayDraft = '';
  }

  removePlanDay(value: string): void {
    this.planForm.dayList = this.planForm.dayList.filter(item => item !== value);
  }

  terminalCode(id?: number): string {
    return id ? id.toString().padStart(5, '0') : '-';
  }

  statusLabel(status?: string): string {
    return status ? status.replace('_', ' ') : 'Sin estado';
  }

  formatDate(value?: string): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      hourCycle: 'h23'
    }).format(date);
  }

  private create(): void {
    this.begin('create');
    this.api
      .createPosTerminal(this.form)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Terminal creada correctamente.';
          this.closeModal();
          this.loadPosTerminals();
        },
        error: error => this.handleError(error, 'No se pudo crear la terminal.')
      });
  }

  private update(id: number): void {
    this.begin('update');
    this.api
      .updatePosTerminal(id, this.form)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Terminal actualizada.';
          this.closeModal();
          this.loadPosTerminals();
        },
        error: error => this.handleError(error, 'No se pudo actualizar la terminal.')
      });
  }

  private createMerchant(): void {
    this.merchantForm.acquirer = 'PAYWAY';
    this.begin('create');
    this.api
      .createMerchantId(this.merchantForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Merchant ID creado correctamente.';
          this.closeMerchantModal();
          this.loadMerchantIds();
        },
        error: error => this.handleError(error, 'No se pudo crear el merchant ID.')
      });
  }

  private updateMerchant(id: number): void {
    this.begin('update');
    this.api
      .updateMerchantId(id, this.merchantForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Merchant ID actualizado.';
          this.closeMerchantModal();
          this.loadMerchantIds();
        },
        error: error => this.handleError(error, 'No se pudo actualizar el merchant ID.')
      });
  }

  private submitCondition(): void {
    if (this.editingCondition?.id) {
      this.updateCondition(this.editingCondition.id);
      return;
    }

    this.createCondition();
  }

  private conditionFeeDuplicateSearch(): string {
    const merchantId = this.conditionForm.posMerchantId;
    const paymentType = this.conditionForm.paymentType;
    const installmentType = this.conditionForm.installmentType;
    if (!merchantId || !paymentType || !installmentType) {
      return '';
    }
    if (paymentType === 'CREDIT') {
      return `posMerchantId==${merchantId};paymentType==${paymentType};installmentType==${installmentType}`;
    }
    if (paymentType === 'DEBIT' || paymentType === 'PREPAID') {
      return `posMerchantId==${merchantId};paymentType==${paymentType}`;
    }
    return '';
  }

  private conditionFeeDuplicateMessage(): string {
    const paymentType = this.conditionForm.paymentType;
    const installmentType = this.conditionForm.installmentType;
    if (paymentType === 'CREDIT') {
      return `El merchant ID ya tiene una condicion CREDIT ${installmentType}.`;
    }
    return `El merchant ID ya tiene una condicion ${paymentType}.`;
  }

  private createCondition(): void {
    this.begin('create');
    this.api
      .createConditionFee(this.conditionForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Merchant condition fee creado correctamente.';
          this.closeConditionModal();
          this.loadConditionFees();
        },
        error: error => this.handleError(error, 'No se pudo crear el merchant condition fee.')
      });
  }

  private updateCondition(id: number): void {
    this.begin('update');
    this.api
      .updateConditionFee(id, this.conditionForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Merchant condition fee actualizado.';
          this.closeConditionModal();
          this.loadConditionFees();
        },
        error: error => this.handleError(error, 'No se pudo actualizar el merchant condition fee.')
      });
  }

  private createInstallment(): void {
    this.begin('create');
    this.api
      .createInstallment(this.installmentForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Installment creado correctamente.';
          this.closeInstallmentModal();
          this.loadInstallments();
        },
        error: error => this.handleError(error, 'No se pudo crear el installment.')
      });
  }

  private updateInstallment(id: number): void {
    this.begin('update');
    this.api
      .updateInstallment(id, this.installmentForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Installment actualizado.';
          this.closeInstallmentModal();
          this.loadInstallments();
        },
        error: error => this.handleError(error, 'No se pudo actualizar el installment.')
      });
  }

  private createPlan(): void {
    this.begin('create');
    this.api
      .createInstallmentsPlan(this.planForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Plan creado correctamente.';
          this.closePlanModal();
          this.loadPlans();
        },
        error: error => this.handleError(error, 'No se pudo crear el plan.')
      });
  }

  private updatePlan(id: number): void {
    this.begin('update');
    this.api
      .updateInstallmentsPlan(id, this.planForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Plan actualizado.';
          this.closePlanModal();
          this.loadPlans();
        },
        error: error => this.handleError(error, 'No se pudo actualizar el plan.')
      });
  }

  private createPlanFactor(): void {
    this.begin('create');
    this.api
      .createInstallmentsPlanFactor(this.planFactorForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Installment de plan creado correctamente.';
          this.closePlanFactorModal();
          this.loadPlanFactors();
        },
        error: error => this.handleError(error, 'No se pudo crear el installment de plan.')
      });
  }

  private updatePlanFactor(id: number): void {
    this.begin('update');
    this.api
      .updateInstallmentsPlanFactor(id, this.planFactorForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Installment de plan actualizado.';
          this.closePlanFactorModal();
          this.loadPlanFactors();
        },
        error: error => this.handleError(error, 'No se pudo actualizar el installment de plan.')
      });
  }

  private createAssignPlan(): void {
    this.begin('create');
    this.api
      .createClientInstallmentsPlan(this.assignPlanForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Plan asignado correctamente.';
          this.closeAssignPlanModal();
          this.loadAssignPlans();
        },
        error: error => this.handleError(error, 'No se pudo asignar el plan.')
      });
  }

  private updateAssignPlan(id: number): void {
    this.begin('update');
    this.api
      .updateClientInstallmentsPlan(id, this.assignPlanForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Asignacion de plan actualizada.';
          this.closeAssignPlanModal();
          this.loadAssignPlans();
        },
        error: error => this.handleError(error, 'No se pudo actualizar la asignacion de plan.')
      });
  }

  private createVirtual(): void {
    this.begin('create');
    this.api
      .createVirtualTerminal(this.virtualForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Terminal virtual creada correctamente.';
          this.closeVirtualModal();
          this.loadVirtualTerminals();
        },
        error: error => this.handleError(error, 'No se pudo crear la terminal virtual.')
      });
  }

  private updateVirtual(id: number): void {
    this.begin('update');
    this.api
      .updateVirtualTerminal(id, this.virtualForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Terminal virtual actualizada.';
          this.closeVirtualModal();
          this.loadVirtualTerminals();
        },
        error: error => this.handleError(error, 'No se pudo actualizar la terminal virtual.')
      });
  }

  private createVirtualMedioPago(): void {
    this.begin('create');
    this.api
      .createVirtualTerminalMedioPago(this.virtualMedioPagoForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Medio de pago asociado correctamente.';
          this.closeVirtualMedioPagoModal();
          this.loadVirtualTerminalMedioPagos();
        },
        error: error => this.handleError(error, 'No se pudo asociar el medio de pago.')
      });
  }

  private updateVirtualMedioPago(id: number): void {
    this.begin('update');
    this.api
      .updateVirtualTerminalMedioPago(id, this.virtualMedioPagoForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Medio de pago actualizado.';
          this.closeVirtualMedioPagoModal();
          this.loadVirtualTerminalMedioPagos();
        },
        error: error => this.handleError(error, 'No se pudo actualizar el medio de pago.')
      });
  }

  private createTerminalPayway(): void {
    this.begin('create');
    this.api
      .createTerminalPayway(this.terminalPaywayForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Terminal Payway creada correctamente.';
          this.closeTerminalPaywayModal();
          this.loadTerminalPayways();
        },
        error: error => this.handleError(error, 'No se pudo crear la terminal Payway.')
      });
  }

  private updateTerminalPayway(id: number): void {
    this.begin('update');
    this.api
      .updateTerminalPayway(id, this.terminalPaywayForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Terminal Payway actualizada.';
          this.closeTerminalPaywayModal();
          this.loadTerminalPayways();
        },
        error: error => this.handleError(error, 'No se pudo actualizar la terminal Payway.')
      });
  }

  private createTerminalGP(): void {
    this.begin('create');
    this.api.createTerminalGP(this.terminalGPForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Terminal Global Processing creada correctamente.';
          this.closeTerminalGPModal();
          this.loadTerminalGPs();
        },
        error: error => this.handleError(error, 'No se pudo crear la Terminal Global Processing.')
      });
  }

  private updateTerminalGP(id: number): void {
    this.begin('update');
    this.api.updateTerminalGP(id, this.terminalGPForm)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Terminal Global Processing actualizada.';
          this.closeTerminalGPModal();
          this.loadTerminalGPs();
        },
        error: error => this.handleError(error, 'No se pudo actualizar la Terminal Global Processing.')
      });
  }

  private begin(action: TerminalAction): void {
    this.loadingAction = action;
    this.message = '';
    this.error = '';
  }

  private finish(): void {
    this.loadingAction = undefined;
    this.cdr.markForCheck();
  }

  private cleanFilters(): Record<string, TerminalFilterValue> {
    return Object.entries(this.filters).reduce<Record<string, TerminalFilterValue>>((acc, [key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        acc[key] = value;
      } else if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private buildMerchantRsql(): string {
    const expressions: string[] = [];
    const filters = this.cleanMerchantFilters();

    this.appendRsql(expressions, filters['id'], 'id==%s');
    this.appendRsql(expressions, filters['clientId'], 'clientId==%s');
    this.appendRsql(expressions, filters['merchantId'], 'merchantId==*%s*');
    this.appendRsql(expressions, filters['descripcion'], 'descripcion==*%s*');
    this.appendRsql(expressions, filters['mcc'], 'mcc==*%s*');
    this.appendRsql(expressions, filters['bankId'], 'bankId==%s');
    this.appendRsql(expressions, filters['cardBrand'], 'cardBrand==%s');
    this.appendRsql(expressions, filters['paymentProcessType'], 'paymentProcessType==%s');
    this.appendRsql(expressions, filters['acquirer'], 'acquirer==%s');
    this.appendRsql(expressions, filters['enabled'], 'enabled==%s');

    if (this.merchantSearchText.trim()) {
      const value = this.sanitizeRsqlValue(this.merchantSearchText);
      expressions.push(`(merchantId==*${value}*,descripcion==*${value}*,mcc==*${value}*)`);
    }

    return expressions.join(';');
  }

  private buildConditionRsql(): string {
    const expressions: string[] = [];
    const filters = this.cleanConditionFilters();

    this.appendRsql(expressions, filters['id'], 'id==%s');
    this.appendRsql(expressions, filters['posMerchantId'], 'posMerchantId==%s');
    this.appendRsql(expressions, filters['paymentType'], 'paymentType==%s');
    this.appendRsql(expressions, filters['installmentType'], 'installmentType==%s');

    if (this.conditionSearchText.trim()) {
      const value = this.sanitizeRsqlValue(this.conditionSearchText);
      if (/^\d+$/.test(value)) {
        expressions.push(`(id==${value},posMerchantId==${value})`);
      } else {
        expressions.push(`(paymentType==*${value}*,installmentType==*${value}*)`);
      }
    }

    return expressions.join(';');
  }

  private buildInstallmentRsql(): string {
    const expressions: string[] = [];
    const filters = this.cleanInstallmentFilters();

    this.appendRsql(expressions, filters['id'], 'id==%s');
    this.appendRsql(expressions, filters['posMerchantConditionFeeId'], 'posMerchantConditionFeeId==%s');
    this.appendRsql(expressions, filters['installmentNumber'], 'installmentNumber==%s');
    this.appendRsql(expressions, filters['enable'], 'enable==%s');

    if (this.installmentSearchText.trim()) {
      const value = this.sanitizeRsqlValue(this.installmentSearchText);
      if (/^\d+$/.test(value)) {
        expressions.push(`(id==${value},posMerchantConditionFeeId==${value},installmentNumber==${value})`);
      }
    }

    return expressions.join(';');
  }

  private buildPlanRsql(): string {
    const expressions: string[] = [];
    const filters = this.cleanPlanFilters();

    this.appendRsql(expressions, filters['id'], 'id==%s');
    this.appendRsql(expressions, filters['posMerchantConditionFeeId'], 'posMerchantConditionFeeId==%s');
    this.appendRsql(expressions, filters['description'], 'description==*%s*');
    this.appendRsql(expressions, filters['enabled'], 'enabled==%s');

    if (this.planSearchText.trim()) {
      const value = this.sanitizeRsqlValue(this.planSearchText);
      const conditions = [`description==*${value}*`];
      if (/^\d+$/.test(value)) {
        conditions.push(`id==${value}`, `posMerchantConditionFeeId==${value}`);
      }
      expressions.push(`(${conditions.join(',')})`);
    }

    return expressions.join(';');
  }

  private buildPlanFactorRsql(): string {
    const expressions: string[] = [];
    const filters = this.cleanPlanFactorFilters();

    this.appendRsql(expressions, filters['id'], 'id==%s');
    this.appendRsql(expressions, filters['posInstallmentsPlanId'], 'posInstallmentsPlanId==%s');
    this.appendRsql(expressions, filters['installmentNumber'], 'installmentNumber==%s');
    this.appendRsql(expressions, filters['displayText'], 'displayText==*%s*');
    this.appendRsql(expressions, filters['enabled'], 'enabled==%s');

    if (this.planFactorSearchText.trim()) {
      const value = this.sanitizeRsqlValue(this.planFactorSearchText);
      const conditions = [`displayText==*${value}*`];
      if (/^\d+$/.test(value)) {
        conditions.push(`id==${value}`, `posInstallmentsPlanId==${value}`, `installmentNumber==${value}`);
      }
      expressions.push(`(${conditions.join(',')})`);
    }

    return expressions.join(';');
  }

  private buildAssignPlanRsql(): string {
    const expressions: string[] = [];
    const filters = this.cleanAssignPlanFilters();

    this.appendRsql(expressions, filters['id'], 'id==%s');
    this.appendRsql(expressions, filters['clientId'], 'clientId==%s');
    this.appendRsql(expressions, filters['posInstallmentsPlanId'], 'posInstallmentsPlanId==%s');
    this.appendRsql(expressions, filters['enabled'], 'enabled==%s');

    if (this.assignPlanSearchText.trim()) {
      const value = this.sanitizeRsqlValue(this.assignPlanSearchText);
      if (/^\d+$/.test(value)) {
        expressions.push(`(id==${value},clientId==${value},posInstallmentsPlanId==${value})`);
      }
    }

    return expressions.join(';');
  }

  private buildVirtualRsql(): string {
    const expressions: string[] = [];
    const filters = this.cleanVirtualFilters();

    this.appendRsql(expressions, filters['id'], 'id==%s');
    this.appendRsql(expressions, filters['posTerminalId'], 'posTerminalId==%s');
    this.appendRsql(expressions, filters['terminalType'], 'terminalType==%s');
    this.appendRsql(expressions, filters['clientId'], 'clientId==%s');
    this.appendRsql(expressions, filters['terminalId'], 'terminalId==*%s*');
    this.appendRsql(expressions, filters['status'], 'status==%s');
    this.appendRsql(expressions, filters['enabled'], 'enabled==%s');

    if (this.virtualSearchText.trim()) {
      const value = this.sanitizeRsqlValue(this.virtualSearchText);
      const conditions = [`terminalId==*${value}*`];
      if (/^\d+$/.test(value)) {
        conditions.push(`id==${value}`, `posTerminalId==${value}`, `clientId==${value}`);
      }
      expressions.push(`(${conditions.join(',')})`);
    }

    return expressions.join(';');
  }

  private buildVirtualMedioPagoRsql(): string {
    const expressions: string[] = [];
    const filters = this.cleanVirtualMedioPagoFilters();

    this.appendRsql(expressions, filters['id'], 'id==%s');
    this.appendRsql(expressions, filters['pos_virutal_terminal_id'], 'pos_virutal_terminal_id==%s');
    this.appendRsql(expressions, filters['medio_pago_id'], 'medio_pago_id==%s');

    if (this.virtualMedioPagoSearchText.trim()) {
      const value = this.sanitizeRsqlValue(this.virtualMedioPagoSearchText);
      if (/^\d+$/.test(value)) {
        expressions.push(`(id==${value},pos_virutal_terminal_id==${value},medio_pago_id==${value})`);
      }
    }

    return expressions.join(';');
  }

  private buildTerminalPaywayRsql(): string {
    const expressions: string[] = [];
    const filters = this.cleanTerminalPaywayFilters();

    this.appendRsql(expressions, filters['id'], 'id==%s');
    this.appendRsql(expressions, filters['terminalId'], 'terminalId==*%s*');
    this.appendRsql(expressions, filters['posVirtualTerminalId'], 'posVirtualTerminalId==%s');
    this.appendRsql(expressions, filters['posMerchantId'], 'posMerchantId==%s');

    if (this.terminalPaywaySearchText.trim()) {
      const value = this.sanitizeRsqlValue(this.terminalPaywaySearchText);
      const conditions = [`terminalId==*${value}*`];
      if (/^\d+$/.test(value)) {
        conditions.push(`id==${value}`, `posVirtualTerminalId==${value}`, `posMerchantId==${value}`);
      }
      expressions.push(`(${conditions.join(',')})`);
    }

    return expressions.join(';');
  }

  private buildTerminalGPRsql(): string {
    const expressions: string[] = [];
    const filters = this.cleanTerminalGPFilters();
    this.appendRsql(expressions, filters['id'], 'id==%s');
    this.appendRsql(expressions, filters['posVirtualTerminalId'], 'posVirtualTerminalId==%s');
    this.appendRsql(expressions, filters['submerchantDataId'], 'submerchantDataId==%s');

    const value = this.sanitizeRsqlValue(this.terminalGPSearchText.trim());
    if (value && /^\d+$/.test(value)) {
      expressions.push(`(id==${value},posVirtualTerminalId==${value},submerchantDataId==${value})`);
    }
    return expressions.join(';');
  }

  private buildConfigurationRsql(): string {
    const expressions: string[] = [];
    const filters = this.cleanConfigurationFilters();

    this.appendRsql(expressions, filters['terminalId'], 'id==%s');
    this.appendRsql(expressions, filters['serialNumber'], 'serialNumber==*%s*');
    this.appendRsql(expressions, filters['softwareVersion'], 'softwareVersion==*%s*');
    this.appendRsql(expressions, filters['terminalStatus'], 'terminalStatus==%s');
    this.appendRsql(expressions, filters['terminalEnabled'], 'enabled==%s');

    if (this.configurationSearchText.trim()) {
      const value = this.sanitizeRsqlValue(this.configurationSearchText);
      const conditions = [`serialNumber==*${value}*`, `softwareVersion==*${value}*`];
      if (/^\d+$/.test(value)) {
        conditions.push(`id==${value}`);
      }
      expressions.push(`(${conditions.join(',')})`);
    }

    return expressions.join(';');
  }

  private appendRsql(expressions: string[], value: TerminalFilterValue | undefined, template: string): void {
    if (value === null || value === undefined || value === '') {
      return;
    }
    expressions.push(template.replace('%s', this.sanitizeRsqlValue(String(value))));
  }

  private cleanMerchantFilters(): Record<string, TerminalFilterValue> {
    return Object.entries(this.merchantFilters).reduce<Record<string, TerminalFilterValue>>((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private cleanConditionFilters(): Record<string, TerminalFilterValue> {
    return Object.entries(this.conditionFilters).reduce<Record<string, TerminalFilterValue>>((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private cleanInstallmentFilters(): Record<string, TerminalFilterValue> {
    return Object.entries(this.installmentFilters).reduce<Record<string, TerminalFilterValue>>((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private cleanPlanFilters(): Record<string, TerminalFilterValue> {
    return Object.entries(this.planFilters).reduce<Record<string, TerminalFilterValue>>((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private cleanPlanFactorFilters(): Record<string, TerminalFilterValue> {
    return Object.entries(this.planFactorFilters).reduce<Record<string, TerminalFilterValue>>((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private cleanAssignPlanFilters(): Record<string, TerminalFilterValue> {
    return Object.entries(this.assignPlanFilters).reduce<Record<string, TerminalFilterValue>>((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private cleanVirtualFilters(): Record<string, TerminalFilterValue> {
    return Object.entries(this.virtualFilters).reduce<Record<string, TerminalFilterValue>>((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private cleanVirtualMedioPagoFilters(): Record<string, TerminalFilterValue> {
    return Object.entries(this.virtualMedioPagoFilters).reduce<Record<string, TerminalFilterValue>>((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private cleanTerminalPaywayFilters(): Record<string, TerminalFilterValue> {
    return Object.entries(this.terminalPaywayFilters).reduce<Record<string, TerminalFilterValue>>((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private cleanTerminalGPFilters(): Record<string, TerminalFilterValue> {
    return Object.entries(this.terminalGPFilters).reduce<Record<string, TerminalFilterValue>>((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') acc[key] = value;
      return acc;
    }, {});
  }

  private cleanConfigurationFilters(): Record<string, TerminalFilterValue> {
    return Object.entries(this.configurationFilters).reduce<Record<string, TerminalFilterValue>>((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private loadAvailablePosTerminals(): void {
    this.api.getAvailablePosTerminals().subscribe({
      next: response => {
        this.availablePosTerminals = response ?? [];
        this.cdr.markForCheck();
      },
      error: error => this.handleError(error, 'No se pudo cargar la lista de terminales POS habilitadas.')
    });
  }

  private ensureVirtualTerminalOptions(): void {
    if (this.virtualTerminalOptions.length) {
      return;
    }
    this.api.getVirtualTerminals(0, 500, '').subscribe({
      next: response => {
        const terminals = response.content ?? [];
        this.virtualTerminalOptions = terminals;
        const posTerminalIds = [...new Set(terminals
          .map(terminal => terminal.posTerminalId)
          .filter((id): id is number => id != null))];
        if (posTerminalIds.length) {
          this.api.getPosTerminalsByIds(posTerminalIds).subscribe({
            next: response => {
              this.virtualPosTerminalSerials = new Map((response.content ?? [])
                .filter(terminal => terminal.id != null)
                .map(terminal => [terminal.id!, terminal.serialNumber ?? '']));
              this.cdr.markForCheck();
            },
            error: error => this.handleError(error, 'No se pudieron cargar los seriales de las terminales.')
          });
        }
        this.cdr.markForCheck();
      },
      error: error => this.handleError(error, 'No se pudo cargar la lista de terminales virtuales.')
    });
  }

  private ensureMerchantOptions(): void {
    if (this.merchantOptions.length) {
      return;
    }
    this.api.getMerchantIds(0, 500, '').subscribe({
      next: response => {
        this.merchantOptions = response.content ?? [];
        this.cdr.markForCheck();
      },
      error: error => this.handleError(error, 'No se pudo cargar la lista de merchant ID.')
    });
  }

  private ensureSubmerchantOptions(): void {
    if (this.submerchantOptions.length) return;
    this.api.getSubmerchants(0, 500, 'cnp==false').subscribe({
      next: response => {
        this.submerchantOptions = response.content ?? [];
        this.cdr.markForCheck();
      },
      error: error => this.handleError(error, 'No se pudo cargar la lista de submerchants.')
    });
  }

  private ensureInstallmentConditionFeeOptions(): void {
    if (this.installmentConditionFeeOptions.length) {
      return;
    }
    this.ensureMerchantOptions();
    this.api.getConditionFees(0, 500, 'installmentType==INSTALLMENTS').subscribe({
      next: response => {
        this.installmentConditionFeeOptions = response.content ?? [];
        this.cdr.markForCheck();
      },
      error: error => this.handleError(error, 'No se pudo cargar la lista de condition fee con cuotas.')
    });
  }

  private ensurePlanOptions(): void {
    if (this.planOptions.length) {
      return;
    }
    this.api.getInstallmentsPlans(0, 500, '').subscribe({
      next: response => {
        this.planOptions = response.content ?? [];
        this.cdr.markForCheck();
      },
      error: error => this.handleError(error, 'No se pudo cargar la lista de planes.')
    });
  }

  private sanitizeRsqlValue(value: string): string {
    return value.trim().replaceAll(';', '').replaceAll(',', '').replaceAll('(', '').replaceAll(')', '');
  }

  private handleError(error: unknown, fallback: string): void {
    console.error(fallback, error);
    this.error = this.errorMessage(error, fallback);
    this.cdr.markForCheck();
  }

  private errorMessage(error: unknown, fallback: string): string {
    if (typeof error === 'object' && error && 'error' in error) {
      const body = (error as {
        error?: {
          errors?: Array<{ description?: string; message?: string; detail?: string }>;
          message?: string;
          detail?: string;
          error?: string;
        };
      }).error;
      const descriptions = body?.errors
        ?.map(item => item.description ?? item.message ?? item.detail)
        .filter((description): description is string => Boolean(description));
      if (descriptions?.length) {
        return descriptions.join('\n');
      }
      return body?.message ?? body?.detail ?? body?.error ?? fallback;
    }
    return fallback;
  }

  private emptyForm(): PosTerminalSaveRequest {
    return {
      serialNumber: '',
      softwareVersion: '',
      enabled: true,
      terminalStatus: 'FUNCIONAL'
    };
  }

  private emptyMerchantForm(): PosMerchantIdSaveRequest {
    return {
      clientId: null,
      merchantId: '',
      enabled: true,
      descripcion: '',
      mcc: '',
      cardBrand: 'VISA',
      paymentProcessType: 'PAYFAC',
      bankId: null,
      acquirer: this.selectedAcquirer ?? 'PAYWAY'
    };
  }

  private emptyConditionForm(): PosMerchantConditionFeeSaveRequest {
    return {
      posMerchantId: null,
      paymentType: '',
      installmentType: '',
      tax: null,
      fee: null,
      tna: null,
      bankFee: null
    };
  }

  private emptyInstallmentForm(): PosInstallmentsSaveRequest {
    return {
      posMerchantConditionFeeId: null,
      installmentFactor: null,
      installmentNumber: null,
      start: '',
      enable: true
    };
  }

  private emptyPlanForm(): PosInstallmentsPlanSaveRequest {
    return {
      posMerchantConditionFeeId: null,
      description: '',
      internacional: false,
      start: '',
      ending: '',
      enabled: true,
      clientFinancialCharge: false,
      surchargePercentage: null,
      maxFinancingAmount: null,
      binList: [],
      dayList: []
    };
  }

  private emptyPlanFactorForm(): PosInstallmentsPlanFactorSaveRequest {
    return {
      posInstallmentsPlanId: null,
      installmentFactor: null,
      installmentNumber: null,
      displayText: '',
      enabled: true
    };
  }

  private emptyAssignPlanForm(): PosClientInstallmentsPlanSaveRequest {
    return {
      clientId: null,
      posInstallmentsPlanId: null,
      enabled: true
    };
  }

  private emptyVirtualForm(): PosVirtualTerminalSaveRequest {
    return {
      posTerminalId: null,
      terminalType: 'CP',
      clientId: null,
      status: 'ONLINE',
      enabled: true
    };
  }

  private emptyVirtualMedioPagoForm(): PosVirtualTerminalMedioPagoSaveRequest {
    return {
      pos_virutal_terminal_id: null,
      medio_pago_id: null
    };
  }

  private emptyTerminalPaywayForm(): PosTerminalPaywaySaveRequest {
    return {
      terminalId: '',
      posVirtualTerminalId: null,
      posMerchantId: null
    };
  }

  private emptyTerminalGPForm(): PosTerminalGPSaveRequest {
    return {
      posVirtualTerminalId: null,
      submerchantDataId: null
    };
  }

  private emptySubmerchantForm(): SubmerchantSaveRequest {
    return {
      clientId: null,
      idLocalidad: null,
      cnp: false,
      submerchantDTO: {
		mcc: null, cuit: null, razonSocial: '', nombreComercial: '', softDescriptor: '', calleNumero: '',
        codPostal: '', localidad: '', provincia: '', nombreTitular: '', apellidoTitular: '', tipoDocumento: 1,
		documentoTitular: null, estado: 0, mailComercio: '', urlSitioWeb: '', telefono: null,
		fechaNacimientoTitular: ''
      }
    };
  }
}
